/**
 * Rota `/casos/:id` — resolve o destino do caso e redireciona (E78.F1.T03).
 *
 * Até a E78 esta rota era um placeholder ("Detalhe do caso") e a lista escolhia
 * a rota sozinha, sem olhar a modalidade. Agora ela é o ÚNICO endereço que a
 * lista, a esteira e qualquer link interno precisam conhecer: carrega o caso
 * (e, na raia eletrônica, as peças) e manda para a tela em que o trabalho está.
 *
 * O redirecionamento é `replace`: quem chega aqui não pode encontrar esta rota
 * de novo ao voltar no navegador — voltaria a resolver e a empurrar adiante.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { destinoDoCaso, rotaDoDestino } from '@/lib/caso_destino';
import { CaseError, getCase } from '@/lib/cases';
import { listExamesEletronicos, type ExameEletronicoCcbData } from '@/lib/exame_eletronico';

const t = ptBR.casoDestino;

export function CasoDestinoPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const navigate = useNavigate();
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (caseId === '') return;
    let cancelado = false;
    void (async () => {
      try {
        const caso = await getCase(client, caseId);
        // A lista da raia só existe (e só responde) na modalidade eletrônica:
        // pedi-la para um caso grafotécnico devolveria 422 e transformaria uma
        // navegação boa numa tela de erro.
        const raia =
          caso.modalidade === 'eletronica' ? await listExamesEletronicos(client, caseId) : null;
        const pecas: ExameEletronicoCcbData[] = raia?.pecas ?? [];
        if (cancelado) return;
        navigate(rotaDoDestino(destinoDoCaso(caso, pecas), caseId), { replace: true });
      } catch (err) {
        if (cancelado) return;
        setErro(err instanceof CaseError && err.reason === 'not_found' ? t.naoEncontrado : t.erro);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId, navigate]);

  return (
    <section data-testid="caso-destino-page" aria-labelledby="caso-destino-title">
      <h1 id="caso-destino-title" className="text-display-md text-fg">
        {t.titulo}
      </h1>
      {erro !== null ? (
        <p
          className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="caso-destino-erro"
        >
          {erro}
        </p>
      ) : (
        <p className="mt-sc-4 text-body-md text-fg-muted" data-testid="caso-destino-carregando">
          {t.carregando}
        </p>
      )}
    </section>
  );
}
